import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

import Button from '../../../components/ui/Button';
import featuredContentService from '../../../services/featuredContentService';
import LiquidGradientBackground from './LiquidGradientBackground';
import { useLogoSettings } from '../../../hooks/useLogoSettings';

const HeroSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [featuredContent, setFeaturedContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const { logoIcon } = useLogoSettings();

  // Load featured content from database
  useEffect(() => {
    const loadFeaturedContent = async () => {
      try {
        const { data, error } = await featuredContentService?.getAllFeaturedContent();
        if (error) throw new Error(error);
        
        if (data && data?.length > 0) {
          setFeaturedContent(data);
        } else {
          // Fallback to default content if no database content
          setFeaturedContent([
            {
              id: 'default-1',
              title: 'Your Passion / Precisely Tracked',
              subtitle: 'Turn your obsession into a sophisticated portfolio',
              image_path: null
            },
            {
              id: 'default-2', 
              title: 'Where Collecting / Meets Investing',
              subtitle: 'Smart decisions backed by real-time intelligence',
              image_path: null
            },
            {
              id: 'default-3',
              title: 'Rare Finds / Smart Decisions',
              subtitle: 'Share the stories and protect your investments',
              image_path: null
            }
          ]);
        }
      } catch (error) {
        console.error('Error loading featured content:', error);
        // Use fallback content on error
        setFeaturedContent([
          {
            id: 'fallback-1',
            title: 'Your Passion / Precisely Tracked',
            subtitle: 'Turn your obsession into a sophisticated portfolio',
            image_path: null
          }
        ]);
      } finally {
        setLoading(false);
      }
    };

    loadFeaturedContent();
  }, []);

  // Auto-rotation effect - left to right transition
  useEffect(() => {
    if (featuredContent?.length === 0) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % featuredContent?.length);
    }, 5000); // 5 second intervals

    return () => clearInterval(interval);
  }, [featuredContent?.length]);

  const currentContent = featuredContent?.[currentIndex];

  const scrollToFeatures = () => {
    document.getElementById('features-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  if (loading) {
    return (
      <section className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-muted/20">
        <div className="flex items-center space-x-3 text-muted-foreground">
          <div className="w-6 h-6 border-2 border-primary/20 border-t-primary rounded-full animate-spin"></div>
          <span>Loading...</span>
        </div>
      </section>
    );
  }

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Liquid Gradient Background — always rendered beneath everything */}
      <div className="absolute inset-0">
        <LiquidGradientBackground />
      </div>
      {/* Full Background Images - Covers entire viewport above Discover More */}
      <div className="absolute inset-0" style={{ bottom: '80px' }}>
        <AnimatePresence mode="wait">
          {currentContent?.imageUrl && currentContent?.imageUrl !== null && currentContent?.imageUrl !== 'null' ? (
            <motion.div
              key={`bg-${currentContent?.id}-${currentIndex}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ 
                duration: 1.2, 
                ease: "easeInOut",
                opacity: { duration: 1.2 }
              }}
              className="absolute inset-0 w-full h-full"
            >
              <div 
                className="absolute inset-0 w-full h-full bg-cover bg-center bg-no-repeat"
                style={{
                  backgroundImage: `url("${currentContent?.imageUrl}")`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  filter: 'brightness(0.4) contrast(1.1)',
                  width: '100%',
                  height: '100%'
                }}
              />
              {/* Enhanced overlay for better text readability */}
              <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/40 to-black/60" />
            </motion.div>
          ) : null}
        </AnimatePresence>
      </div>
      {/* Content Layer */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 text-center">
        <div className="max-w-4xl mx-auto">
          {/* Logo Animation — pulse scale 0.95 → 1.05, 2s ping-pong */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="mb-8"
          >
            <div className="flex justify-center mb-6">
              <motion.img
                src={logoIcon}
                alt="Crazy Rare Club Symbol"
                className="w-32 h-32 object-contain"
                loading="eager"
                fetchpriority="high"
                decoding="async"
                animate={{ scale: [0.95, 1.05, 0.95] }}
                transition={{
                  duration: 2,
                  ease: "easeInOut",
                  repeat: Infinity,
                  repeatType: "loop"
                }}
              />
            </div>
          </motion.div>

          {/* Animated Featured Content - Synchronized with background transitions */}
          <div className="mb-12 overflow-hidden">
            <AnimatePresence mode="wait">
              <motion.div
                key={`text-${currentContent?.id}-${currentIndex}`}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, filter: 'blur(4px)' }}
                transition={{ 
                  duration: 1.2,
                  ease: "easeInOut",
                  opacity: { duration: 1.2, ease: "easeInOut" },
                  y: { duration: 0.6, ease: "easeOut" },
                  filter: { duration: 0.5, ease: "easeOut" }
                }}
                className="text-center"
              >
                <h2 className="text-4xl lg:text-6xl font-bold text-white leading-tight mb-6 drop-shadow-2xl">
                  {currentContent?.title?.split(' / ')?.map((line, index) => (
                    <React.Fragment key={index}>
                      {line}
                      {index < currentContent?.title?.split(' / ')?.length - 1 && <br />}
                    </React.Fragment>
                  ))}
                </h2>
                <p className="text-xl lg:text-2xl text-white/90 max-w-3xl mx-auto leading-relaxed drop-shadow-lg">
                  {currentContent?.subtitle}
                </p>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Progress Indicators */}
          {featuredContent?.length > 1 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 0.5 }}
              className="flex justify-center space-x-2 mb-8"
            >
              {featuredContent?.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    index === currentIndex 
                      ? 'bg-white scale-125' :'bg-white/30 hover:bg-white/50'
                  }`}
                />
              ))}
            </motion.div>
          )}

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6 mb-16"
          >
            <Button
              variant="default"
              size="lg"
              iconName="ArrowRight"
              iconPosition="right"
              className="px-8 py-4 text-lg font-semibold liquid-gold premium-hover"
              onClick={() => window.location.href = '/registration'}
            >
              Start Your Collection
            </Button>
            
            <Button
              variant="outline"
              size="lg"
              iconName="Play"
              iconPosition="left"
              className="px-8 py-4 text-lg font-medium premium-hover border-white/30 text-white hover:bg-white hover:text-black"
              onClick={scrollToFeatures}
            >
              See How It Works
            </Button>
          </motion.div>

          {/* Trust Indicators */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.6 }}
            className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 max-w-4xl mx-auto"
          >
            <div className="text-center px-2">
              <div className="text-3xl font-bold text-primary mb-2 drop-shadow-lg">15K+</div>
              <div className="text-sm text-white/80 drop-shadow-sm">Active Collectors</div>
            </div>
            <div className="text-center px-2">
              <div className="text-3xl font-bold text-primary mb-2 drop-shadow-lg">$2.4M</div>
              <div className="text-sm text-white/80 drop-shadow-sm">Portfolio Value</div>
            </div>
            <div className="text-center px-2">
              <div className="text-3xl font-bold text-primary mb-2 drop-shadow-lg">98%</div>
              <div className="text-sm text-white/80 drop-shadow-sm">Accuracy Rate</div>
            </div>
            <div className="text-center px-2">
              <div className="text-3xl font-bold text-primary mb-2 drop-shadow-lg">24/7</div>
              <div className="text-sm text-white/80 drop-shadow-sm">Market Tracking</div>
            </div>
          </motion.div>
        </div>
      </div>
      {/* Scroll Indicator - Discover More */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1 }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20"
      >
        <button
          onClick={scrollToFeatures}
          className="flex flex-col items-center space-y-2 text-white/80 hover:text-white transition-colors duration-300 premium-hover drop-shadow-lg"
        >
          <span className="text-sm font-medium">Discover More</span>
          <div className="w-6 h-10 border-2 border-current rounded-full flex justify-center">
            <div className="w-1 h-3 bg-current rounded-full mt-2 animate-bounce"></div>
          </div>
        </button>
      </motion.div>
    </section>
  );
};

export default HeroSection;